const logicProductModel = globalThis.LogicStampModel || (typeof require !== 'undefined' ? require('./logic-stamp-picker.js') : null);
const logicProductSeriesOrder = ['element','zinc','prism','rollo','delta-v','forza','strata'];
function logicProductParseVariant(variant,tags = []) {
  return logicProductModel ? logicProductModel.parse(variant,tags) : {...variant,supported:false};
}
function logicProductSelectionDimensions(options,variants) {
  return logicProductModel ? logicProductModel.dimensions(options,variants) : new Set();
}
function logicProductSeriesTarget(variants,current,series,dimensions,preferredWeight = '') {
  return logicProductModel?.target(variants,current,dimensions,{series,stamp:current.stamp},preferredWeight) || null;
}
function logicProductSwipeDirection(start,end) {
  const dx = end.x-start.x, dy = end.y-start.y;
  if (end.time-start.time > 900 || Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)*1.6) return 0;
  return dx < 0 ? 1 : -1;
}
class LogicProductSeries extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    if (this.abort || this.unsubscribe) return;
    this.productInfo = this.closest('product-info');
    if (!this.productInfo) return;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({data}) => {
      if (String(data.sectionId) !== String(this.productInfo.dataset.section)) return;
      const replacement = data.html?.querySelector('logic-product-series');
      if (replacement) this.replaceWith(document.importNode(replacement,true));
      else this.hidden = true;
    });
    if (this.dataset.enabled !== 'true') return;
    this.picker = this.productInfo?.querySelector('variant-selects');
    const script = this.querySelector('[data-series-data]');
    if (!this.picker || !script) return;
    try { this.data = JSON.parse(script.textContent); } catch { return; }
    this.variants = this.data.variants.map(variant => logicProductParseVariant(variant,this.data.tags));
    this.current = this.variants.find(variant => String(variant.id) === this.dataset.selectedVariant);
    if (!this.current?.supported) return;
    if (this.current.stamp === 'ACL' && ['R','L','H'].includes(this.current.weight) && (!this.productInfo.querySelector('logic-stamp-picker[data-ready="true"]') || !this.productInfo.dataset.logicProWeight)) this.productInfo.dataset.logicProWeight = this.current.weight;
    this.preferredWeight = this.productInfo.dataset.logicProWeight || this.current.weight;
    this.dimensions = logicProductSelectionDimensions(this.data.options,this.variants);
    this.entries = logicProductSeriesOrder.map(series => ({series,variant:logicProductSeriesTarget(this.variants,this.current,series,this.dimensions,this.preferredWeight)})).filter(entry => entry.variant || entry.series === this.current.series);
    this.index = this.entries.findIndex(entry => entry.series === this.current.series);
    if (this.entries.length < 2 || this.index < 0) return;
    this.abort = new AbortController();
    this.querySelector('[data-series-controls]').hidden = false;
    this.dataset.ready = 'true';
    this.tabIndex = 0;
    this.setAttribute('role','group');
    this.busy = false;
    this.sync();
    const options = {signal:this.abort.signal};
    this.addEventListener('click',event => {
      const button = event.target.closest('[data-series-direction]');
      if (button) this.select(Number(button.dataset.seriesDirection),true);
    },options);
    this.addEventListener('keydown',event => {
      if (event.target !== this && !event.target.closest('[data-series-direction]')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.select(event.key === 'ArrowRight' ? 1 : -1,true);
      }
    },options);
    this.addEventListener('pointerdown',event => {
      if (!event.isPrimary || !['touch','pen'].includes(event.pointerType) || event.target.closest('button,a,input,select,textarea')) return;
      this.gesture = {x:event.clientX,y:event.clientY,time:event.timeStamp,id:event.pointerId};
      this.setPointerCapture(event.pointerId);
    },options);
    this.addEventListener('pointermove',event => {
      if (!this.gesture || event.pointerId !== this.gesture.id) return;
      if (Math.abs(event.clientY-this.gesture.y) > 12 && Math.abs(event.clientY-this.gesture.y) > Math.abs(event.clientX-this.gesture.x)) this.gesture = null;
    },options);
    this.addEventListener('pointerup',event => {
      const start = this.gesture;
      this.gesture = null;
      if (!start || start.id !== event.pointerId) return;
      const direction = logicProductSwipeDirection(start,{x:event.clientX,y:event.clientY,time:event.timeStamp});
      if (direction) this.select(direction,false);
    },options);
    this.addEventListener('pointercancel',() => {this.gesture = null;},options);
    this.productInfo.addEventListener('product-info:error',() => {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.querySelector('[data-series-error]').hidden = false;
      delete this.productInfo.dataset.logicSeriesFocus;
      this.sync();
    },options);
    const focusDirection = this.productInfo.dataset.logicSeriesFocus;
    if (focusDirection) requestAnimationFrame(() => {
      if (!this.isConnected) return;
      const button = this.querySelector('[data-series-direction="' + focusDirection + '"]');
      (button && !button.disabled ? button : this).focus({preventScroll:true});
      delete this.productInfo.dataset.logicSeriesFocus;
    });
  }
  disconnectedCallback() { this.abort?.abort(); this.abort = null; this.unsubscribe?.(); this.unsubscribe = null; }
  sync() {
    this.querySelectorAll('[data-series-direction]').forEach(button => {
      const target = this.entries[this.index + Number(button.dataset.seriesDirection)];
      button.disabled = this.busy || !target?.variant;
    });
    this.querySelector('[data-series-position]').textContent = this.data.position.replace('__CURRENT__',this.index+1).replace('__TOTAL__',this.entries.length);
  }
  select(direction,restoreFocus) {
    if (this.busy) return;
    const entry = this.entries[this.index+direction];
    if (!entry?.variant) return;
    const stampPicker = this.productInfo.querySelector('logic-stamp-picker[data-ready="true"]');
    if (stampPicker) {
      if (stampPicker.busy) return;
      if (restoreFocus) this.productInfo.dataset.logicSeriesFocus = String(direction);
      if (stampPicker.selectVariant(entry.variant)) {
        this.busy = true;
        this.setAttribute('aria-busy','true');
        this.sync();
      }
      return;
    }
    const picker = this.productInfo.querySelector('variant-selects');
    const groups = [...picker.querySelectorAll('.product-form__input')];
    const selections = entry.variant.options.map((value,index) => {
      const group = groups[index];
      if (!group) return null;
      const select = group.querySelector('select');
      const control = select ? [...select.options].find(option => option.value === value) : [...group.querySelectorAll('input[type="radio"]')].find(input => input.value === value);
      return control ? {group,select,control,value,index} : null;
    });
    if (selections.some(selection => !selection)) {
      this.querySelector('[data-series-error]').hidden = false;
      return;
    }
    for (const {group,select,control,value} of selections) {
      if (select) {
        select.value = value;
        [...select.options].forEach(option => option === control ? option.setAttribute('selected','selected') : option.removeAttribute('selected'));
      } else group.querySelectorAll('input[type="radio"]').forEach(input => {input.checked = input === control;});
    }
    const changed = selections.find(selection => this.dimensions.has(selection.index) && this.current.options[selection.index] !== selection.value) || selections[0];
    this.busy = true;
    this.setAttribute('aria-busy','true');
    this.querySelector('[data-series-error]').hidden = true;
    if (restoreFocus) this.productInfo.dataset.logicSeriesFocus = String(direction);
    this.sync();
    (changed.select || changed.control).dispatchEvent(new Event('change',{bubbles:true}));
  }
}
if (typeof customElements !== 'undefined' && !customElements.get('logic-product-series')) customElements.define('logic-product-series',LogicProductSeries);
if (typeof module !== 'undefined' && module.exports) module.exports = {logicProductParseVariant,logicProductSelectionDimensions,logicProductSeriesTarget,logicProductSwipeDirection,LogicProductSeries};
